import {
  AttachTestEvidence,
  ConcludeTestArgs,
  CreateTest,
  EngagementId,
  Paginated,
  PaginationArgs,
  RunStepArgs,
  SampleId,
  SetTestExtractionDecision,
  Test,
  TestCriterionId,
  TestEvidence,
  TestLinkedControlOe,
  TestEvidenceId,
  TestExtractionFieldId,
  TestId,
  TestItemId,
  TestMaterializedItem,
  TestMin,
  TestResultCounts,
  TestTemplateId,
  UpdateTestConclusion,
  UpdateTestResultDecision,
  UpsertTestCriterion,
  UpsertTestExtractionField,
} from "platformed-client/types";
import {invalidateQueries, q, queryClient} from "../../state";
import {platformedClient} from "..";

/**
 * Mints a draft test. Both references are optional and, when supplied, only
 * attached: nothing is materialized and no sample is locked until {@link ready}.
 */
export async function create(engagementId: EngagementId, body: CreateTest): Promise<Test> {
  const test = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/tests",
    {path: {engagement_id: engagementId}, body},
  );
  // "Linked tests" lists drafts, so an attached sample goes stale even though a
  // draft locks nothing.
  await invalidateQueries([
    q.tprm.engagementTests.filter(engagementId),
    q.tprm.engagementSamples.filter(),
    ...(test.sample
      ? [
          q.tprm.sample.filter(test.sample.sample_id),
          q.tprm.sampleTests.filter(test.sample.sample_id),
          q.tprm.populationSamples.filter(),
        ]
      : []),
  ]);
  return test;
}

/**
 * Points a draft test at a template, for the first time or as a re-point.
 * Rejected (409) once the test is ready: the pinned version's element ids are
 * what its config, results and conclusion key off.
 */
export async function changeTemplate(testId: TestId, templateId: TestTemplateId): Promise<Test> {
  const test = await platformedClient.internalRequest("post", "/region/{region}/api/tprm/tests/{test_id}/template", {
    path: {test_id: testId},
    body: {test_template_id: templateId},
  });
  await invalidateQueries([q.tprm.engagementTests.filter(), q.tprm.test.filter(testId)]);
  return test;
}

/**
 * Stops a draft test following a template, leaving it authored with no criteria;
 * a test with no pin is left as it is.
 * Rejected (409) once the test is ready, as a re-point is.
 */
export async function clearTemplate(testId: TestId): Promise<void> {
  await platformedClient.internalRequest("delete", "/region/{region}/api/tprm/tests/{test_id}/template", {
    path: {test_id: testId},
  });
  await invalidateQueries([q.tprm.engagementTests.filter(), q.tprm.test.filter(testId)]);
}

/**
 * The test's commit verb: freezes a snapshot of the sample's items, locking
 * that sample, and leaves the template pin and config as the draft shaped them.
 * Rejected (409) when the sample is missing, when the resolved procedure has no
 * criterion, when the sample has not finished being drawn, and on a second
 * call.
 */
export async function ready(testId: TestId): Promise<Test> {
  const test = await platformedClient.internalRequest("post", "/region/{region}/api/tprm/tests/{test_id}/ready", {
    path: {test_id: testId},
  });
  // The test gains its config and items, and its sample becomes locked.
  await invalidateQueries([
    q.tprm.test.filter(testId),
    q.tprm.testItems.filter(testId),
    q.tprm.testResultCounts.filter(testId),
    q.tprm.engagementTests.filter(),
    q.tprm.engagementSamples.filter(),
    q.tprm.sample.filter(),
    q.tprm.sampleTests.filter(),
  ]);
  return test;
}

export async function list(engagementId: EngagementId): Promise<TestMin[]> {
  return await platformedClient.internalRequest("get", "/region/{region}/api/tprm/engagements/{engagement_id}/tests", {
    path: {engagement_id: engagementId},
  });
}

export async function listForSample(sampleId: SampleId): Promise<TestMin[]> {
  return await platformedClient.internalRequest("get", "/region/{region}/api/tprm/samples/{sample_id}/tests", {
    path: {sample_id: sampleId},
  });
}

export async function get(testId: TestId): Promise<Test> {
  return await platformedClient.internalRequest("get", "/region/{region}/api/tprm/tests/{test_id}", {
    path: {test_id: testId},
  });
}

export async function updateName(testId: TestId, name: string): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/tests/{test_id}/name", {
    path: {test_id: testId},
    body: {name},
  });
  await invalidateQueries([q.tprm.engagementTests.filter(), q.tprm.sampleTests.filter(), q.tprm.test.filter(testId)]);
}

/** `null` clears the description; the column holds no empty string. */
export async function updateDescription(testId: TestId, description: string | null): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/tests/{test_id}/description", {
    path: {test_id: testId},
    body: {description},
  });
  await invalidateQueries([q.tprm.engagementTests.filter(), q.tprm.sampleTests.filter(), q.tprm.test.filter(testId)]);
}

export async function listItems(testId: TestId, query: PaginationArgs): Promise<Paginated<TestMaterializedItem>> {
  return await platformedClient.internalRequest("get", "/region/{region}/api/tprm/tests/{test_id}/items", {
    path: {test_id: testId},
    query,
  });
}

export async function listLinkedControls(testId: TestId): Promise<TestLinkedControlOe[]> {
  return await platformedClient.internalRequest("get", "/region/{region}/api/tprm/tests/{test_id}/linked-controls", {
    path: {test_id: testId},
  });
}

export async function getResultCounts(testId: TestId): Promise<TestResultCounts> {
  return await platformedClient.internalRequest("get", "/region/{region}/api/tprm/tests/{test_id}/result-counts", {
    path: {test_id: testId},
  });
}

export async function listEvidence(testId: TestId): Promise<TestEvidence[]> {
  return await platformedClient.internalRequest("get", "/region/{region}/api/tprm/tests/{test_id}/evidence", {
    path: {test_id: testId},
  });
}

// Refresh a test: the test itself, its materialized items (criteria, extracted
// data, results and evidence live there), the counts over their results, and
// its attachment list.
async function invalidateTest(testId: TestId): Promise<void> {
  await invalidateQueries([
    q.tprm.test.filter(testId),
    q.tprm.testItems.filter(testId),
    q.tprm.testResultCounts.filter(testId),
    q.tprm.testEvidence.filter(testId),
  ]);
}

// Upsert (override or add) a single criterion in a test's config. Each row edit
// is its own write; concurrent edits are safe because edit transactions are
// serializable server-side.
export async function upsertCriterion(
  testId: TestId,
  criterionId: TestCriterionId,
  body: UpsertTestCriterion,
): Promise<void> {
  await platformedClient.internalRequest("put", "/region/{region}/api/tprm/tests/{test_id}/criteria/{criterion_id}", {
    path: {test_id: testId, criterion_id: criterionId},
    body,
  });
  await invalidateTest(testId);
}

export async function deleteCriterion(testId: TestId, criterionId: TestCriterionId): Promise<void> {
  await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/tests/{test_id}/criteria/{criterion_id}",
    {path: {test_id: testId, criterion_id: criterionId}},
  );
  await invalidateTest(testId);
}

// Return a test's criteria to its pinned template's. Scoped to that dimension:
// the extraction fields, which a background run writes, are left alone.
export async function resetCriteria(testId: TestId): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/tests/{test_id}/criteria/reset", {
    path: {test_id: testId},
  });
  await invalidateTest(testId);
}

// Upsert (override or add) a single extraction field in a test's config. The
// server stamps the field's author, so no caller supplies one.
export async function upsertExtractionField(
  testId: TestId,
  fieldId: TestExtractionFieldId,
  body: UpsertTestExtractionField,
): Promise<void> {
  await platformedClient.internalRequest(
    "put",
    "/region/{region}/api/tprm/tests/{test_id}/extraction-fields/{field_id}",
    {path: {test_id: testId, field_id: fieldId}, body},
  );
  await invalidateTest(testId);
}

export async function deleteExtractionField(testId: TestId, fieldId: TestExtractionFieldId): Promise<void> {
  await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/tests/{test_id}/extraction-fields/{field_id}",
    {path: {test_id: testId, field_id: fieldId}},
  );
  await invalidateTest(testId);
}

// Return a test's extraction fields to its pinned template's. Scoped to that
// dimension: the criteria are left alone.
export async function resetExtractionFields(testId: TestId): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/tests/{test_id}/extraction-fields/reset", {
    path: {test_id: testId},
  });
  await invalidateTest(testId);
}

export async function changeSample(testId: TestId, sampleId: SampleId): Promise<Test> {
  const test = await platformedClient.internalRequest("post", "/region/{region}/api/tprm/tests/{test_id}/sample", {
    path: {test_id: testId},
    body: {sample_id: sampleId},
  });
  // Change-sample latches the sample it moves to; the one it leaves keeps its
  // lock. The tests lists carry the sample's name and size as their own copies.
  await invalidateQueries([
    q.tprm.test.filter(testId),
    q.tprm.testItems.filter(testId),
    q.tprm.testResultCounts.filter(testId),
    q.tprm.engagementSamples.filter(),
    q.tprm.populationSamples.filter(),
    q.tprm.sample.filter(),
    q.tprm.engagementTests.filter(),
    q.tprm.sampleTests.filter(),
  ]);
  return test;
}

export async function lock(testId: TestId): Promise<Test> {
  const test = await platformedClient.internalRequest("post", "/region/{region}/api/tprm/tests/{test_id}/lock", {
    path: {test_id: testId},
  });
  await invalidateTest(testId);
  return test;
}

export async function unlock(testId: TestId): Promise<Test> {
  const test = await platformedClient.internalRequest("post", "/region/{region}/api/tprm/tests/{test_id}/unlock", {
    path: {test_id: testId},
  });
  await invalidateTest(testId);
  return test;
}

export async function detachEvidence(testId: TestId, evidenceId: TestEvidenceId): Promise<void> {
  await platformedClient.internalRequest("delete", "/region/{region}/api/tprm/tests/{test_id}/evidence/{evidence_id}", {
    path: {test_id: testId, evidence_id: evidenceId},
  });
  await invalidateTest(testId);
}

export async function attachEvidence(testId: TestId, body: AttachTestEvidence): Promise<Test> {
  // Enqueues MapTestEvidence; returns the test (the running step shows on it).
  const test = await platformedClient.internalRequest("post", "/region/{region}/api/tprm/tests/{test_id}/evidence", {
    path: {test_id: testId},
    body,
  });
  await invalidateTest(testId);
  return test;
}

export async function draftProcedures(testId: TestId): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/tests/{test_id}/draft-procedures", {
    path: {test_id: testId},
  });
  await invalidateTest(testId);
}

export async function suggestFields(testId: TestId): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/tests/{test_id}/suggest-fields", {
    path: {test_id: testId},
  });
  await invalidateTest(testId);
}

// A step run refused for replacing output refetches too, so the confirmation
// opens over the output it would replace.

export async function extract(testId: TestId, body: RunStepArgs): Promise<void> {
  try {
    await platformedClient.internalRequest("post", "/region/{region}/api/tprm/tests/{test_id}/extract", {
      path: {test_id: testId},
      body,
    });
  } finally {
    await invalidateTest(testId);
  }
}

export async function run(testId: TestId, body: RunStepArgs): Promise<void> {
  try {
    await platformedClient.internalRequest("post", "/region/{region}/api/tprm/tests/{test_id}/run", {
      path: {test_id: testId},
      body,
    });
  } finally {
    await invalidateTest(testId);
  }
}

export async function conclude(testId: TestId, body: ConcludeTestArgs): Promise<void> {
  try {
    await platformedClient.internalRequest("post", "/region/{region}/api/tprm/tests/{test_id}/conclude", {
      path: {test_id: testId},
      body,
    });
  } finally {
    await invalidateTest(testId);
  }
}

/**
 * Writes or withdraws the auditor's conclusion. The generated lane is untouched
 * either way, so a conclude run in flight collides with nothing. The resolved
 * test comes back whole; the lists carry the resolved effectiveness too.
 */
export async function updateConclusion(testId: TestId, body: UpdateTestConclusion): Promise<Test> {
  const test = await platformedClient.internalRequest("put", "/region/{region}/api/tprm/tests/{test_id}/conclusion", {
    path: {test_id: testId},
    body,
  });
  await invalidateQueries([q.tprm.test.filter(testId), q.tprm.engagementTests.filter(), q.tprm.sampleTests.filter()]);
  return test;
}

/** Writes the auditor's outcome or exception note on one result; a blank note clears it. */
export async function updateResultDecision(
  testId: TestId,
  itemId: TestItemId,
  criterionId: TestCriterionId,
  body: UpdateTestResultDecision,
): Promise<TestMaterializedItem> {
  const item = await platformedClient.internalRequest(
    "put",
    "/region/{region}/api/tprm/tests/{test_id}/items/{test_item_id}/results/{criterion_id}",
    {path: {test_id: testId, test_item_id: itemId, criterion_id: criterionId}, body},
  );
  // The response is current only for the result it wrote: a save of another
  // result on the same item can commit later and answer first.
  patchCachedItem(testId, itemId, i => ({...i, decisions: withEntry(i.decisions, criterionId, item.decisions)}));
  await invalidateQueries([q.tprm.testResultCounts.filter(testId)]);
  return item;
}

function patchCachedItem(
  testId: TestId,
  itemId: TestItemId,
  patch: (item: TestMaterializedItem) => TestMaterializedItem,
) {
  queryClient.setQueriesData<Paginated<TestMaterializedItem>>(
    q.tprm.testItems.filter(testId),
    page => page && {...page, items: page.items.map(i => (i.test_item_id === itemId ? patch(i) : i))},
  );
}

/** `cached` with its `key` entry taken from `served`, or dropped where `served` has none. */
function withEntry<V>(cached: {[key: string]: V}, key: string, served: {[key: string]: V}): {[key: string]: V} {
  const {[key]: _, ...others} = cached;
  const entry = served[key];
  return entry === undefined ? others : {...others, [key]: entry};
}

/**
 * Writes the auditor's value into one extracted cell; a `null` value states
 * None (stated). A value equal to the AI's withdraws the auditor's instead.
 */
export async function setExtractionDecision(
  testId: TestId,
  itemId: TestItemId,
  fieldId: TestExtractionFieldId,
  body: SetTestExtractionDecision,
): Promise<TestMaterializedItem> {
  const item = await platformedClient.internalRequest(
    "put",
    "/region/{region}/api/tprm/tests/{test_id}/items/{test_item_id}/extracted/{field_id}",
    {path: {test_id: testId, test_item_id: itemId, field_id: fieldId}, body},
  );
  // Current only for the cell it wrote, as in `updateResultDecision`.
  patchCachedItem(testId, itemId, i => ({
    ...i,
    extraction_decisions: withEntry(i.extraction_decisions, fieldId, item.extraction_decisions),
  }));
  return item;
}

/** Withdraws the auditor's value from one extracted cell: Revert to AI. */
export async function deleteExtractionDecision(
  testId: TestId,
  itemId: TestItemId,
  fieldId: TestExtractionFieldId,
): Promise<TestMaterializedItem> {
  const item = await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/tests/{test_id}/items/{test_item_id}/extracted/{field_id}",
    {path: {test_id: testId, test_item_id: itemId, field_id: fieldId}},
  );
  patchCachedItem(testId, itemId, i => ({
    ...i,
    extraction_decisions: withEntry(i.extraction_decisions, fieldId, item.extraction_decisions),
  }));
  return item;
}

export async function delete_(testId: TestId): Promise<void> {
  await platformedClient.internalRequest("delete", "/region/{region}/api/tprm/tests/{test_id}", {
    path: {test_id: testId},
  });
  // Invalidate the lists and the samples' linked-test counts. We don't know the
  // sample id here, so invalidate the sample queries broadly (none are deleted,
  // so they refetch safely). Never invalidate the deleted test's own queries —
  // those would 404 on the now-gone id; let them be GC'd.
  await invalidateQueries([
    q.tprm.engagementTests.filter(),
    q.tprm.engagementSamples.filter(),
    q.tprm.populationSamples.filter(),
    q.tprm.sample.filter(),
    q.tprm.sampleTests.filter(),
  ]);
}
